import { pipe, OperatorFunction } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { Result } from 'neverthrow';

import { ErrorSinkService, ErrorSeverity, DisplayableError } from './error-sink.service';

// ─── handleModbusError RxJS operator ──────────────────────────────────────────
//
// Transforms Observable<Result<T, E>> into Observable<T | undefined>,
// routing errors to the ErrorSink.
//
// Usage:
//   this.data$ = modbusRead.read$(channel, refresh$, table).pipe(
//     handleModbusError(errorSink, 'battery-config'),
//   );
//
// On ok(value): emits value
// On err(error): reports to ErrorSink, emits undefined
//
// Severity inference: transport errors → 'toast', protocol errors → 'silent'.
// Protocol errors (register not supported) are expected during normal operation
// and should not generate user-visible toast notifications.

interface HandleErrorOptions {
  /** Override severity (default: 'toast' for transport, 'silent' for protocol). */
  severity?: ErrorSeverity;
  /** i18n key or message for the toast title. */
  title?: string;
  /** i18n key or message for the toast body. If omitted, uses a default. */
  message?: string;
  /** Called when the error is a skippable protocol error (register not supported). */
  onUnsupported?: () => void;
}

/**
 * RxJS operator that extracts Ok values and routes Err values to ErrorSink.
 *
 * @param errorSink The ErrorSinkService instance
 * @param context Label for error identification (e.g. component/section name)
 * @param options Optional severity/message overrides
 */
export function handleModbusError<T, E extends { kind: string; message?: string; description?: string }>(
  errorSink: ErrorSinkService,
  context: string,
  options?: HandleErrorOptions,
): OperatorFunction<Result<T, E>, T | undefined> {
  return pipe(
    tap((result: Result<T, E>) => {
      if (result.isErr()) {
        const error = result.error;

        // Notify caller when register is not supported by device (0x01=IllegalFunction, 0x02=IllegalDataAddress)
        const modbusException = (error as any).modbusException;
        if (error.kind === 'protocol' && options?.onUnsupported && (modbusException === 0x01 || modbusException === 0x02)) {
          options.onUnsupported();
        }

        const severity = options?.severity ?? inferSeverity(error);
        const displayError: DisplayableError = {
          code: `${error.kind.toUpperCase()}_${context}`,
          message: options?.message ?? defaultMessage(error),
          title: options?.title,
          severity,
          source: error.kind === 'transport' ? 'transport' : 'protocol',
          technicalDetail: `[${context}] ${(error as any).description ?? (error as any).reason ?? error.kind}`,
          timestamp: Date.now(),
        };
        errorSink.report(displayError);
      }
    }),
    map((result: Result<T, E>) => result.isOk() ? result.value : undefined),
  );
}

function inferSeverity(error: { kind: string }): ErrorSeverity {
  return error.kind === 'transport' ? 'toast' : 'silent';
}

function defaultMessage(error: { kind: string; message?: string; description?: string }): string {
  if (error.kind === 'transport') {
    return 'Communication error. Please try again.';
  }
  return (error as any).description ?? 'An error occurred';
}
